export interface BookMeetingParams {
  client_id: string;
  user_id: string;
  topic: string;
  start_time: string;
  timezone?: string;
  agenda?: string;
  attendees?: string[];
  location?: string;
  meeting_id?: string;
  lead_id?: string;
  booked_channel?: string;
  sales_person?: string;
  sales_member_id?: string;
}

export interface BookMeetingResponse {
  success: boolean;
  error?: string;
  action?: string;
  meeting_id?: string;
  calendar_event?: {
    provider: string;
    event_id: string;
    html_link?: string;
    web_link?: string;
  };
  conference?: {
    provider: string;
    join_url: string;
    meeting_id?: string;
  } | null;
}

export interface RescheduleMeetingParams {
  client_id: string;
  user_id: string;
  lead_id: string;
  start_time: string;
  timezone?: string;
  topic?: string;
  sales_member_id?: string;
}

export interface RescheduleMeetingResponse {
  success: boolean;
  error?: string;
  action?: string;
  meeting_id?: string;
  new_date?: string;
  new_time?: string;
  meeting_link?: string;
}

export interface CancelMeetingResponse {
  success: boolean;
  error?: string;
  action?: string;
  meeting_id?: string;
}

export interface RecordingUrlResponse {
  success: boolean;
  error?: string;
  video_url?: string;
  audio_url?: string;
}

export interface TranscriptSegment {
  speaker: string;
  timestamp: string;
  text: string;
}

export interface PrepareMeetingRemindersParams {
  lead_id: string;
  client_id: string;
  user_id: string;
  type: "new" | "rescheduled" | "cancelled";
  meeting_datetime?: string;
  meeting_link?: string;
  booked_agent?: string;
  timezone?: string;
  source?: "ui" | "agent";
  additional_emails?: string[];
}

const getApiHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
});

const getApiUrl = (fn: string) =>
  `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${fn}`;

export const meetingService = {
  bookMeeting: async (
    params: BookMeetingParams
  ): Promise<BookMeetingResponse> => {
    try {
      const response = await fetch(getApiUrl("book-meeting"), {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify({ ...params, action: "book" }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return { success: false, error: data.error || "Failed to book meeting" };
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  rescheduleMeeting: async (
    params: RescheduleMeetingParams
  ): Promise<RescheduleMeetingResponse> => {
    try {
      const response = await fetch(getApiUrl("book-meeting"), {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify({ ...params, action: "reschedule" }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || "Failed to reschedule meeting",
        };
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  cancelMeeting: async (params: {
    client_id: string;
    user_id: string;
    lead_id: string;
  }): Promise<CancelMeetingResponse> => {
    try {
      const response = await fetch(getApiUrl("book-meeting"), {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify({ ...params, action: "cancel" }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || "Failed to cancel meeting",
        };
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  getRecordingUrl: async (
    meetingId: string
  ): Promise<RecordingUrlResponse> => {
    try {
      const response = await fetch(getApiUrl("get-recording-url"), {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify({ meeting_id: meetingId }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || "Failed to get recording URL",
        };
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },

  parseTranscript: (transcriptRaw: string): TranscriptSegment[] => {
    try {
      const parsed = JSON.parse(transcriptRaw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  },

  prepareMeetingReminders: async (
    params: PrepareMeetingRemindersParams
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(getApiUrl("prepare-meeting-reminders"), {
        method: "POST",
        headers: getApiHeaders(),
        body: JSON.stringify(params),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        return {
          success: false,
          error: data.error || "Failed to prepare meeting reminders",
        };
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  },
};
